// src/components/admin/AdminUsers.js
import React, { useState, useEffect } from 'react';
import {
  Search,
  Shield,
  User,
  Mail,
  Phone,
  Calendar,
  Star,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import AdminService from '../../services/AdminService';

const AdminUsers = ({ isMobile }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [elevatingUser, setElevatingUser] = useState(null);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const allUsers = await AdminService.getAllUsers();
        setUsers(allUsers);
        setFilteredUsers(allUsers);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  useEffect(() => {
    // Filter users based on search term
    if (searchTerm) {
      const filtered = users.filter(user =>
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [users, searchTerm]);

  const handleElevateUser = async (user) => {
    const confirmElevation = window.confirm(
      `Are you sure you want to elevate ${user.displayName || user.email} to admin? This will give them full access to the admin panel.`
    );

    if (!confirmElevation) return;

    try {
      setElevatingUser(user.userId);
      await AdminService.elevateUserToAdmin(user.userId);

      // Update local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.userId === user.userId
            ? { ...u, role: 'admin', isAdmin: true }
            : u
        )
      );

      alert('User successfully elevated to admin!');
    } catch (error) {
      console.error('Error elevating user:', error);
      alert('Failed to elevate user. Please try again.');
    } finally {
      setElevatingUser(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">User Management</h2>

        {/* Search */}
        <div className="flex items-center justify-between mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-[#3A98B9] focus:border-[#3A98B9]"
            />
          </div>

          <div className="text-sm text-gray-600 ml-4">
            {filteredUsers.length} of {users.length} users
          </div>
        </div>

        {/* User Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
              <User className="h-8 w-8 text-blue-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Admin Users</p>
                <p className="text-2xl font-bold">{users.filter(u => u.isAdmin).length}</p>
              </div>
              <Shield className="h-8 w-8 text-green-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Stripe Customers</p>
                <p className="text-2xl font-bold">{users.filter(u => u.hasStripeAccount).length}</p>
              </div>
              <Star className="h-8 w-8 text-purple-200" />
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Total Spent</p>
                <p className="text-2xl font-bold">
                  ${users
                    .filter(u => u.stripeCustomer?.totalSpent)
                    .reduce((sum, u) => sum + u.stripeCustomer.totalSpent, 0)
                    .toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-orange-200" />
            </div>
          </div>
        </div>
      </div>

      {/* Users List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-medium text-gray-900">All Users</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredUsers.length > 0 ? (
            filteredUsers.map((user) => (
              <div key={user.userId} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    {/* User Avatar */}
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-[#3A98B9] flex items-center justify-center">
                        <span className="text-white font-medium">
                          {user.displayName?.charAt(0) || user.firstName?.charAt(0) || user.email?.charAt(0) || 'U'}
                        </span>
                      </div>
                    </div>

                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'No Name'}
                        </p>
                        {user.isAdmin && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <Shield className="h-3 w-3 mr-1" />
                            Admin
                          </span>
                        )}
                        {user.hasStripeAccount && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            <Star className="h-3 w-3 mr-1" />
                            Stripe Customer
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          <span className="truncate">{user.email}</span>
                        </div>

                        {user.phone && (
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-gray-400" />
                            <span>{user.phone}</span>
                          </div>
                        )}

                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                          <span>Joined {formatDate(user.createdAt)}</span>
                        </div>
                      </div>

                      {/* Stripe Analytics */}
                      {user.stripeCustomer && (
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2">
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                            <div>
                              <span className="text-purple-600 font-medium">Total Spent:</span>
                              <span className="block text-purple-900 font-bold">
                                ${user.stripeCustomer.totalSpent?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                              </span>
                            </div>
                            <div>
                              <span className="text-purple-600 font-medium">Transactions:</span>
                              <span className="block text-purple-900 font-bold">{user.stripeCustomer.transactionCount || 0}</span>
                            </div>
                            <div>
                              <span className="text-purple-600 font-medium">Avg. Order:</span>
                              <span className="block text-purple-900 font-bold">
                                ${user.stripeCustomer.averageTransactionAmount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                              </span>
                            </div>
                            <div>
                              <span className="text-purple-600 font-medium">Customer Since:</span>
                              <span className="block text-purple-900 font-bold">
                                {user.stripeCustomer.customerSince ? formatDate(user.stripeCustomer.customerSince) : 'N/A'}
                              </span>
                            </div>
                          </div>
                          {user.stripeCustomer.lastPayment && (
                            <div className="mt-2 pt-2 border-t border-purple-200">
                              <span className="text-purple-600 font-medium text-xs">Last Payment:</span>
                              <span className="ml-2 text-purple-900 text-xs">
                                {formatDate(user.stripeCustomer.lastPayment)}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* User Actions */}
                    <div className="flex-shrink-0">
                      {!user.isAdmin ? (
                        <button
                          onClick={() => handleElevateUser(user)}
                          disabled={elevatingUser === user.userId}
                          className="inline-flex items-center px-3 py-1 border border-[#3A98B9] text-[#3A98B9] rounded-md text-sm hover:bg-[#3A98B9]/5 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {elevatingUser === user.userId ? (
                            <>
                              <div className="animate-spin h-4 w-4 border-b-2 border-[#3A98B9] rounded-full mr-2"></div>
                              Elevating...
                            </>
                          ) : (
                            <>
                              <Shield className="h-4 w-4 mr-1" />
                              Make Admin
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1 rounded-md text-sm bg-green-100 text-green-800">
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Additional User Details */}
                {(user.permissions?.elevated_at || user.role === 'admin') && (
                  <div className="mt-3 pl-14">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-center">
                        <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                        <span className="text-sm text-green-800">
                          Admin privileges granted
                          {user.permissions?.elevated_at && (
                            <span className="ml-1">
                              on {formatDate(user.permissions.elevated_at)}
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="px-6 py-8 text-center">
              <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Try adjusting your search' : 'No users have registered yet'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Warning Notice */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
          <div>
            <h4 className="text-sm font-medium text-yellow-800 mb-1">Important Notice</h4>
            <p className="text-sm text-yellow-700">
              Elevating a user to admin gives them full access to this admin panel, including the ability to view all bookings,
              manage other users, and access sensitive business data. Only grant admin privileges to trusted individuals.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;